import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Bot, Building2, CalendarClock, Contact, Layers, Mail, Megaphone, MessageCircle,
  MessageSquare, RefreshCw, Search, Send, Sparkles, Users, Video,
} from 'lucide-react';
import { SettingsCard, SettingsPage, SettingsSection } from '@/components/settings/SettingsPage';
import { StatusPill } from '@/components/StatusPill';
import { StatTile } from '@/components/StatTile';
import { Button } from '@/components/Button';
import { settingsService, PlanUsage, SeatKey } from '@/services/settings.service';

function relativeDate(iso: string): string {
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days}d`;
}

const pctOf = (used: number, max: number) => (max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0);
const toneOf = (pct: number): 'gold' | 'amber' | 'red' => (pct >= 90 ? 'red' : pct >= 70 ? 'amber' : 'gold');
const money = (currency: string, n: number) => `${currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEAT_ICON: Record<SeatKey, React.ReactNode> = {
  whatsappAccounts: <MessageCircle size={15} />,
  facebookAccounts: <Megaphone size={15} />,
  googleAdsAccounts: <Search size={15} />,
  tiktokAccounts: <Video size={15} />,
  emailAccounts: <Mail size={15} />,
  users: <Users size={15} />,
  botFlows: <Bot size={15} />,
};

function UsageRow({ icon, label, note, used, max }: { icon: React.ReactNode; label: string; note?: string; used: number; max: number }) {
  const unlimited = max <= 0;
  const pct = pctOf(used, max);
  const tone = toneOf(pct);
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <div className="truncate text-sm font-medium text-text-primary">
            {label}
            {note && <span className="ml-1.5 text-xs font-normal text-text-muted">· {note}</span>}
          </div>
          <div className={clsx('shrink-0 text-xs font-semibold tabular-nums', tone === 'red' ? 'text-danger' : tone === 'amber' ? 'text-warning' : 'text-text-secondary')}>
            {used.toLocaleString()}{unlimited ? '' : ` / ${max.toLocaleString()}`}
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
          <div
            className={clsx('h-full rounded-full transition-all duration-500', tone === 'red' ? 'bg-danger' : tone === 'amber' ? 'bg-warning' : 'bg-gold-gradient')}
            style={{ width: `${unlimited ? 0 : pct}%` }}
          />
        </div>
        <div className="mt-1 text-2xs text-text-muted">{unlimited ? 'No limit' : `${pct}% used`}</div>
      </div>
    </div>
  );
}

export default function PlanSettings() {
  const [data, setData] = useState<PlanUsage | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const load = async () => {
    setRefreshing(true);
    setFailed(false);
    try { setData(await settingsService.plan()); }
    catch { setFailed(true); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const usageOf = (key: SeatKey): number => {
    if (!data) return 0;
    return (data.usage as unknown as Record<SeatKey, number | undefined>)[key] ?? 0;
  };

  const header = {
    eyebrow: 'Billing',
    title: 'Plan & usage',
    description: 'Your plan, what this workspace used this month, and the modules it can use.',
    actions: (
      <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />} onClick={load} disabled={refreshing}>
        Refresh usage
      </Button>
    ),
  };

  if (!data) {
    return (
      <SettingsPage {...header}>
        {failed ? (
          <div className="py-7">
            <SettingsCard><p className="text-sm text-text-secondary">Your plan couldn't be loaded. <button type="button" onClick={load} className="font-medium text-primary hover:underline">Try again</button></p></SettingsCard>
          </div>
        ) : (
          <div className="space-y-4 py-6">
            <div className="skeleton h-32 rounded-2xl" />
            <div className="grid grid-cols-2 gap-4 @lg:grid-cols-3 @3xl:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
            <div className="skeleton h-64 rounded-2xl" />
          </div>
        )}
      </SettingsPage>
    );
  }

  const { company, plan, usage, seats } = data;
  const initials = company.name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const expired = new Date(company.expiresAt).getTime() < Date.now();
  const tiles = plan ? [
    { label: 'Users', used: usage.users, max: plan.limits.maxUsers, icon: <Users size={16} /> },
    { label: 'Contacts', used: usage.contacts, max: plan.limits.maxContacts, icon: <Contact size={16} /> },
    { label: 'Campaigns', used: usage.campaignsThisMonth, max: plan.limits.maxCampaignsPerMonth, icon: <Send size={16} /> },
    { label: 'Messages', used: usage.messagesThisMonth, max: plan.limits.maxMessagesPerMonth, icon: <MessageSquare size={16} /> },
    { label: 'AI credits', used: usage.aiCreditsThisMonth, max: plan.limits.aiCreditsPerMonth, icon: <Sparkles size={16} /> },
  ] : [];

  return (
    <SettingsPage {...header}>
      <div className="space-y-4 py-6">
        <SettingsCard>
          <div className="flex flex-col gap-5 @xl:flex-row @xl:items-center">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gold-gradient-soft font-display text-xl font-bold text-primary shadow-soft">
              {initials || <Building2 size={26} />}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display text-lg font-bold tracking-tight text-text-primary">{company.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusPill status={company.status} />
                <span className="rounded-md bg-surface-alt px-1.5 py-0.5 font-mono text-2xs uppercase tracking-wide text-text-secondary">{company.slug}</span>
              </div>
              <div className={clsx('mt-2 inline-flex items-center gap-1.5 text-xs', expired ? 'text-danger' : 'text-text-muted')}>
                <CalendarClock size={13} />
                {new Date(company.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · {relativeDate(company.expiresAt)}
              </div>
            </div>
            <div className="shrink-0 rounded-2xl bg-gold-gradient px-5 py-4 text-on-primary shadow-elevated @xl:min-w-[200px]">
              <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-on-primary/70">Current plan</div>
              <div className="mt-1 font-display text-lg font-bold leading-tight">{plan?.displayName ?? 'No plan'}</div>
              {plan && (
                <div className="mt-1 text-sm text-on-primary/85">
                  <span className="font-semibold tabular-nums">${plan.monthlyPriceUsd}</span><span className="opacity-70"> / month</span>
                </div>
              )}
            </div>
          </div>
        </SettingsCard>

        {plan && (
          <div className="grid grid-cols-2 gap-4 @lg:grid-cols-3 @3xl:grid-cols-5">
            {tiles.map((t) => {
              const pct = pctOf(t.used, t.max);
              return (
                <StatTile
                  key={t.label}
                  label={t.label}
                  value={t.used.toLocaleString()}
                  icon={t.icon}
                  accent={toneOf(pct)}
                  delta={t.max > 0 ? `of ${t.max.toLocaleString()} · ${pct}%` : 'No limit'}
                  trend={pct >= 90 ? 'down' : 'neutral'}
                />
              );
            })}
          </div>
        )}
      </div>

      {!plan && (
        <SettingsSection title="Plan" description="Limits and modules come from the plan Slightor assigns to your workspace.">
          <SettingsCard>
            <p className="text-sm text-text-muted">No plan is assigned to this workspace yet. Contact SlightorCRM support to set one up.</p>
          </SettingsCard>
        </SettingsSection>
      )}

      {plan && (
        <SettingsSection title="Usage this month" description="Counts reset on the 1st of each month. A bar turns amber at 70% and red at 90%.">
          <SettingsCard>
            <div className="grid gap-x-8 gap-y-5 @5xl:grid-cols-2">
              <UsageRow icon={<Users size={15} />} label="Users" used={usage.users} max={plan.limits.maxUsers} />
              <UsageRow icon={<Contact size={15} />} label="Contacts" used={usage.contacts} max={plan.limits.maxContacts} />
              <UsageRow icon={<Send size={15} />} label="Campaigns" note="this month" used={usage.campaignsThisMonth} max={plan.limits.maxCampaignsPerMonth} />
              <UsageRow icon={<MessageSquare size={15} />} label="Message attempts" note="this month" used={usage.messagesThisMonth} max={plan.limits.maxMessagesPerMonth} />
              <UsageRow icon={<Sparkles size={15} />} label="AI credits" note="platform-managed" used={usage.aiCreditsThisMonth} max={plan.limits.aiCreditsPerMonth} />
              <UsageRow icon={<Layers size={15} />} label="Groups" used={usage.groups} max={0} />
            </div>
          </SettingsCard>
        </SettingsSection>
      )}

      {seats && (
        <SettingsSection
          title="Modules & seats"
          description="How many of each module this workspace can use. Contact support to change your allocation."
        >
          <SettingsCard padded={false}>
            <div className="divide-y divide-border">
              {seats.modules.map((m) => {
                const used = usageOf(m.key);
                const included = m.allowed > 0;
                const pct = pctOf(used, m.allowed);
                const tone = toneOf(pct);
                return (
                  <div key={m.key} className="flex items-center gap-3 px-5 py-3.5">
                    <div className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', included ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted')}>
                      {SEAT_ICON[m.key] ?? <Layers size={15} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="truncate text-sm font-medium text-text-primary">{m.label}</div>
                        <div className={clsx('shrink-0 text-xs font-semibold tabular-nums', !included ? 'text-text-muted' : tone === 'red' ? 'text-danger' : tone === 'amber' ? 'text-warning' : 'text-text-secondary')}>
                          {included ? `${used.toLocaleString()} / ${m.allowed.toLocaleString()}` : 'Not included'}
                        </div>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <div className="h-1 min-w-[6rem] flex-1 overflow-hidden rounded-full bg-surface-alt">
                          <div className={clsx('h-full rounded-full', tone === 'red' ? 'bg-danger' : tone === 'amber' ? 'bg-warning' : 'bg-gold-gradient')} style={{ width: `${included ? pct : 0}%` }} />
                        </div>
                        <div className="shrink-0 text-2xs tabular-nums text-text-muted">
                          {money(seats.currency, m.perUnitPrice)} / unit
                          {included && <span className="text-text-secondary"> · {money(seats.currency, m.subtotal)}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SettingsCard>

          <SettingsCard
            footer={(
              <div className="flex w-full items-center justify-between gap-3">
                <div className="text-sm font-semibold text-text-primary">Total / month</div>
                <div className="font-display text-lg font-bold tabular-nums text-text-primary">{money(seats.currency, seats.monthlyTotal)}</div>
              </div>
            )}
          >
            <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Monthly summary</div>
            {/* Custom pricing itemises base + seats; a standard plan is one price with seats included. */}
            <dl className="mt-3 space-y-2 text-sm">
              {seats.usesCustomPricing ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-text-secondary">Base fee</dt>
                    <dd className="font-medium tabular-nums text-text-primary">{money(seats.currency, seats.baseMonthlyFee)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-text-secondary">Module seats</dt>
                    <dd className="font-medium tabular-nums text-text-primary">{money(seats.currency, seats.modules.reduce((sum, m) => sum + m.subtotal, 0))}</dd>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-text-secondary">{plan?.displayName ?? 'Plan'}</dt>
                    <dd className="font-medium tabular-nums text-text-primary">{money(seats.currency, seats.monthlyTotal)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-text-secondary">Module seats</dt>
                    <dd className="text-xs text-text-muted">Included</dd>
                  </div>
                </>
              )}
            </dl>
            {seats.usesCustomPricing && <div className="mt-2 text-2xs text-text-muted">Custom pricing applies to this workspace.</div>}
            <p className="mt-3 text-2xs text-text-muted">Subscriptions are managed manually — contact support for changes or renewal.</p>
          </SettingsCard>
        </SettingsSection>
      )}
    </SettingsPage>
  );
}
